/**
 * Project Repository Link Component
 *
 * Manages repository connection for a specific project
 * Embeds RepositoryConnectForm with project-specific handling
 *
 * Features:
 * - Display current repository connection
 * - Connect new repository to project
 * - Disconnect repository from project
 * - Test repository connection
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Alert, Spinner, Badge } from 'flowbite-react';
import { HiCheckCircle, HiXCircle, HiPlus, HiTrash, HiRefresh } from 'react-icons/hi';

import { RepositoryConnectForm } from './RepositoryConnectForm';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

interface ProjectRepositoryLinkProps {
  clientId: string;
  companyId: string;
  projectId: string;
  onSuccess?: () => void;
}

interface RepositoryConnection {
  Repository_ID: string;
  Repository_Name: string;
  Repository_URL: string;
  Repository_Provider: 'github' | 'gitlab' | 'bitbucket';
  Connection_Status: 'active' | 'disconnected' | 'error';
  Default_Branch: string;
  Last_Sync_Date?: string;
  Created_Date: string;
}

export const ProjectRepositoryLink: React.FC<ProjectRepositoryLinkProps> = ({
  clientId,
  companyId,
  projectId,
  onSuccess
}) => {
  const [repository, setRepository] = useState<RepositoryConnection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const loadRepositoryConnection = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // TODO: /api/projects/{id}/repository endpoint doesn't exist in backend
      // Using SPECIFICATIONS.REPOSITORY as closest match for project-repository link
      const response = await Make_Authorized_API_Call<RepositoryConnection>(
        API_ENDPOINTS.SPECIFICATIONS.REPOSITORY,
        'GET',
        undefined,
        { params: { Project_ID: projectId, Client_ID: clientId, Company_ID: companyId } }
      );

      if (isSuccessResponse(response)) {
        if (response.data?.Records?.[0]) {
          setRepository(response.data.Records[0]);
        } else {
          setRepository(null);
        }
      } else {
        // No repository connected yet - not an error
        setRepository(null);
      }
    } catch (err) {
      console.error('[ProjectRepositoryLink] Error loading repository:', err);
      setError(err instanceof Error ? err.message : 'Failed to load repository connection');
    } finally {
      setIsLoading(false);
    }
  }, [clientId, companyId, projectId]);

  useEffect(() => {
    loadRepositoryConnection();
  }, [loadRepositoryConnection]);

  const handleConnectRepository = useCallback(async () => {
    setShowConnectForm(true);
  }, []);

  const handleConnectionSuccess = useCallback(() => {
    setShowConnectForm(false);
    loadRepositoryConnection();
    onSuccess?.();
  }, [loadRepositoryConnection, onSuccess]);

  const handleCancelConnect = useCallback(() => {
    setShowConnectForm(false);
  }, []);

  const handleDisconnectRepository = useCallback(async () => {
    if (!repository) return;

    if (!window.confirm('Are you sure you want to disconnect this repository from the project?')) {
      return;
    }

    try {
      setIsDisconnecting(true);
      setError(null);

      // TODO: /api/projects/{id}/repository DELETE endpoint doesn't exist in backend
      // Using SPECIFICATIONS.REPOSITORY as closest match for project-repository link
      const response = await Make_Authorized_API_Call(
        API_ENDPOINTS.SPECIFICATIONS.REPOSITORY,
        'DELETE',
        {
          Project_ID: projectId,
          Client_ID: clientId,
          Company_ID: companyId,
          Repository_ID: repository.Repository_ID,
        }
      );

      if (isSuccessResponse(response)) {
        setRepository(null);
        onSuccess?.();
      } else {
        throw new Error(response.message || 'Failed to disconnect repository');
      }
    } catch (err) {
      console.error('[ProjectRepositoryLink] Error disconnecting repository:', err);
      setError(err instanceof Error ? err.message : 'Failed to disconnect repository');
    } finally {
      setIsDisconnecting(false);
    }
  }, [clientId, companyId, projectId, repository, onSuccess]);

  const handleRefresh = useCallback(() => {
    loadRepositoryConnection();
  }, [loadRepositoryConnection]);

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'github':
        return '🐙';
      case 'gitlab':
        return '🦊';
      case 'bitbucket':
        return '🪣';
      default:
        return '📦';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'disconnected':
        return 'gray';
      case 'error':
        return 'failure';
      default:
        return 'gray';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert color="failure" className="mb-4">
        <p className="font-semibold">Error</p>
        <p className="text-sm mt-1">{error}</p>
        <Button size="sm" color="light" onClick={handleRefresh} className="mt-2">
          <HiRefresh className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </Alert>
    );
  }

  if (showConnectForm) {
    return (
      <Card>
        <h4 className="text-lg font-semibold text-gray-900 mb-4">
          Connect Repository to Project
        </h4>
        <RepositoryConnectForm
          clientId={clientId}
          companyId={companyId}
          projectId={projectId}
          onSuccess={handleConnectionSuccess}
          onCancel={handleCancelConnect}
        />
      </Card>
    );
  }

  if (!repository) {
    return (
      <Card>
        <div className="text-center py-8">
          <div className="text-gray-400 mb-4">
            <HiXCircle className="w-16 h-16 mx-auto" />
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            No Repository Connected
          </h4>
          <p className="text-sm text-gray-600 mb-4">
            Connect a code repository to enable automated scanning, documentation generation,
            and CI/CD integration for this project.
          </p>
          <Button color="blue" onClick={handleConnectRepository}>
            <HiPlus className="w-4 h-4 mr-2" />
            Connect Repository
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="text-3xl">{getProviderIcon(repository.Repository_Provider)}</div>
          <div>
            <h4 className="text-lg font-semibold text-gray-900">
              {repository.Repository_Name}
            </h4>
            <p className="text-sm text-gray-600">{repository.Repository_URL}</p>
          </div>
        </div>
        <Badge color={getStatusColor(repository.Connection_Status)} size="sm">
          {repository.Connection_Status}
        </Badge>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
        <div>
          <p className="text-xs text-gray-500">Provider</p>
          <p className="text-sm font-medium capitalize">{repository.Repository_Provider}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Default Branch</p>
          <p className="text-sm font-medium">{repository.Default_Branch}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Connected Date</p>
          <p className="text-sm font-medium">
            {new Date(repository.Created_Date).toLocaleDateString()}
          </p>
        </div>
        {repository.Last_Sync_Date && (
          <div>
            <p className="text-xs text-gray-500">Last Sync</p>
            <p className="text-sm font-medium">
              {new Date(repository.Last_Sync_Date).toLocaleString()}
            </p>
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-4 border-t border-gray-200">
        <Button
          color="light"
          size="sm"
          onClick={handleRefresh}
        >
          <HiRefresh className="w-4 h-4 mr-2" />
          Refresh
        </Button>
        <Button
          color="failure"
          size="sm"
          onClick={handleDisconnectRepository}
          disabled={isDisconnecting}
        >
          {isDisconnecting ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Disconnecting...
            </>
          ) : (
            <>
              <HiTrash className="w-4 h-4 mr-2" />
              Disconnect
            </>
          )}
        </Button>
      </div>

      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-800">
          <strong>Note:</strong> Repository connection enables automated code scanning,
          documentation generation, and standards enforcement for this project.
        </p>
      </div>
    </Card>
  );
};

export default ProjectRepositoryLink;
